import type { GridConfig } from '@/types/hex.types';

// Grid configuration - 5x8 portrait grid
export const GRID_WIDTH = 5;
export const GRID_HEIGHT = 8;
export const HEX_SIZE = 1.0; // Hex size adjusted for grid
export const PLAYER_ROWS = 3; // Rows per player (top 3 = P2, middle 2 neutral, bottom 3 = P1)

// Team size limits
export const MIN_TEAM_SIZE = 2; // Starting units in round 1
export const MAX_TEAM_SIZE = 6; // Maximum units allowed
export const UNITS_PER_ROUND = 1; // Additional unit slots per round after round 1
export const BENCH_SIZE = 8;

/**
 * Calculate max units allowed for a given round
 * Round 1: 2 units, Round 2: 3 units, etc. up to MAX_TEAM_SIZE
 */
export function getMaxUnitsForRound(round: number): number {
  const baseUnits = MIN_TEAM_SIZE;
  const additionalUnits = Math.max(0, round - 1) * UNITS_PER_ROUND;
  return Math.min(baseUnits + additionalUnits, MAX_TEAM_SIZE);
}

// Derived hex dimensions (pointy-top)
export const HEX_WIDTH = Math.sqrt(3) * HEX_SIZE;
export const HEX_HEIGHT = 2 * HEX_SIZE;

// Default grid configuration
export const DEFAULT_GRID_CONFIG: GridConfig = {
  width: GRID_WIDTH,
  height: GRID_HEIGHT,
  hexSize: HEX_SIZE,
  origin: { x: 0, y: 0 },
};

// Game loop
export const FIXED_TIMESTEP = 1000 / 60; // 60 Hz in milliseconds
export const MAX_DELTA_TIME = 250; // Max frame time to prevent spiral of death

// Camera - more angled isometric view
export const CAMERA_FRUSTUM_SIZE = 16; // Sized to fit 5x8 portrait grid
export const CAMERA_MIN_WIDTH = 12; // Minimum horizontal world units visible (for narrow screens)
export const CAMERA_NEAR = 0.1;
export const CAMERA_FAR = 1000;
export const CAMERA_ANGLE = Math.PI / 4; // 45 degrees - more dramatic angle

// Colors - grass green theme like reference
export const COLORS = {
  background: 0x87ceeb, // Sky blue background
  grassGreen: 0x7cb342, // Main grass color
  grassGreenLight: 0x8bc34a, // Lighter grass for variation
  grassGreenDark: 0x689f38, // Darker grass
  hexOutline: 0x558b2f, // Subtle darker green outline
  hexHover: 0xffeb3b, // Yellow highlight
} as const;

// Lighting - brighter for outdoor feel
export const AMBIENT_LIGHT_INTENSITY = 0.8;
export const DIRECTIONAL_LIGHT_INTENSITY = 1.0;

// Game phases timing
export const PREP_PHASE_DURATION = 12; // seconds
export const MAX_BATTLE_DURATION = 120; // seconds

// Combat
export const COMBAT_TICK_RATE = 60; // ticks per second
